import { Divider, Flex, SkeletonProps, Spin, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { isNil } from 'lodash-es'
import head from 'lodash-es/head'
import isEmpty from 'lodash-es/isEmpty'
import isNull from 'lodash-es/isNull'
import isNumber from 'lodash-es/isNumber'
import isUndefined from 'lodash-es/isUndefined'
import noop from 'lodash-es/noop'
import { FunctionComponent, useMemo, useState } from 'react'
import ReactDOMServer from 'react-dom/server'
import styled, { useTheme } from 'styled-components'
import { ApexChartOptions, ApexCharts } from '@/components/common/apex-charts'
import ApexChartsYAxisLabels from '@/components/common/apexcharts/apex-charts-y-axis-labels'
import Blur from '@/components/common/blur'
import { Duration } from '@/components/common/duration-select'
import KolDetailDataBlock from '@/components/kol/common/kol-detail-data-block'
import KolDetailDataBlockSubHeader from '@/components/kol/common/kol-detail-data-block-sub-header'
import KolDetailStatistic, {
  BottomItems,
} from '@/components/kol/common/kol-detail-statistic'
import TimeGranularitySelector, {
  Granularity,
} from '@/components/kol/common/time-granularity-selector'
import Blocker from '@/components/kol/detail/blocker'
import { initialPostFilterConditions } from '@/components/kol/detail/creative-content-analyses/post-list'
import CustomApexChartTooltip from '@/components/kol/detail/engagement-analyses/common/custom-apex-chart-tooltip'
import {
  GRANULARITY_CHART_WIDTH_MAP,
  Series,
  SeriesData,
} from '@/components/kol/detail/engagement-analyses/common/schema'
import {
  ChartContent,
  ChartContentWrapper,
  ChartWrapper,
  LegendColor,
  LegendLabel,
  LegendWrapper,
} from '@/components/kol/detail/engagement-analyses/common/styled'
import { NoData } from '@/components/kol/detail/no-data'
import useKolAnalyze from '@/components/kol/hooks/use-kol-analyze'
import useKolDetailRoute from '@/components/kol/hooks/use-kol-detail-route'
import {
  mockKolMetricPerformanceData,
  mockKolMetricPerformanceNullData,
  mockKolPr,
} from '@/constants/kol-detail/metric-performance'
import { mockFixedEngagementAnalysisData } from '@/constants/kol-detail/mock-data'
import { statisticPlatformSupport } from '@/constants/kol-detail/statistic-platform-support'
import {
  DURATION_MONTH_INTEGER_MAP,
  KolDetailDuration,
} from '@/hooks/use-authorization/routes'
import { useIntl } from '@/i18n/hooks/use-intl'
import type { FormatDateType } from '@/i18n/typing'
import { Nullable } from '@/types/nullable'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { Platform, PlatformShortcode } from '@/utils/convert-platform'
import formatDuration from '@/utils/detail/format-duration'
import { groupInteractiveByGranularity } from '@/utils/detail/group-by-granularity'
import inTimeBoundDate from '@/utils/detail/in-time-bound-date'
import { getBoundaryDateSet } from '@/utils/format-date'
import getStartEndIsoStrings from '@/utils/get-start-end-iso-strings'
import evaluateMetricPR from '@/utils/kol/evaluate-metric-pr'
import mergeZeroData from '@/utils/kol/merge-zero-data'

const mockReelsData = {
  shortInteractive: 1235,
  shortInteractiveRate: 0.18,
  avgShortLike: 1213,
  avgShortComment: 22,
}

const mockKolInfoKpi = {
  ...mockKolMetricPerformanceData.yt,
  avgRepostAndQuoteCount: 12,
}

const mockLikeToCommentRatio = 0.05

interface EngagementAnalysisProps {
  kolInfo?: KolSchemaModel
  isFetchingKolInfo: boolean
  isKolInfoLocked: boolean
  isPublicKolDetailPage: boolean
  showMockData?: boolean
  platform: Platform
  duration: KolDetailDuration
}

const EngagementAnalysis: FunctionComponent<EngagementAnalysisProps> = ({
  kolInfo,
  isFetchingKolInfo,
  isKolInfoLocked,
  isPublicKolDetailPage,
  platform,
  duration,
  showMockData,
}) => {
  const { formatMessage, formatNumber, formatDate } = useIntl()
  const theme = useTheme()
  const [timeGranularity, setTimeGranularity] = useState<Granularity>('week')
  const { searchParams, setSearchParams } = useKolDetailRoute()
  const hiddenData = isKolInfoLocked && !showMockData
  const { analyze } = useKolAnalyze({
    kolId: kolInfo?.id,
    kolDcIdHashed: kolInfo?.dcIdHashed,
    isKolInfoLocked,
    platform: platform.code,
  })

  const statisticLoading: SkeletonProps | boolean = isFetchingKolInfo
    ? {
        active: true,
        title: { width: 150 },
        paragraph: { rows: 1, width: 100 },
      }
    : false

  const kpi = kolInfo?.kpi.find((kpi) => kpi.platform === platform.shortcode)

  const generalData = useMemo(():
    | {
        interactiveRate: Nullable<number>
        interactive: Nullable<number>
        avgLike: Nullable<number>
        avgComment: Nullable<number>
        shareCount: Nullable<number>
        likeToCommentRatio: Nullable<number>
        avgRepostAndQuoteCount: Nullable<number>
      }
    | undefined => {
    if (isUndefined(kpi)) {
      return
    }

    if (duration === Duration.ThreeMonths) {
      return {
        interactiveRate: kpi.interactiveRate3m,
        interactive: kpi.interactive3m,
        avgLike: kpi.avgLike3m,
        avgComment: kpi.avgComment3m,
        shareCount: kpi.avgShare3m,
        likeToCommentRatio: isKolInfoLocked
          ? mockLikeToCommentRatio
          : kpi.likeCommentRatio3m,
        avgRepostAndQuoteCount: isKolInfoLocked
          ? mockKolInfoKpi.avgRepostAndQuoteCount
          : kpi.avgRepostAndQuoteCount3m,
      }
    }

    return {
      interactiveRate: kpi.interactiveRate6m,
      interactive: kpi.interactive6m,
      avgLike: kpi.avgLike6m,
      avgComment: kpi.avgComment6m,
      shareCount: kpi.avgShare6m,
      likeToCommentRatio: isKolInfoLocked
        ? mockLikeToCommentRatio
        : kpi.likeCommentRatio6m,
      avgRepostAndQuoteCount: isKolInfoLocked
        ? mockKolInfoKpi.avgRepostAndQuoteCount
        : kpi.avgRepostAndQuoteCount6m,
    }
  }, [kpi, duration, isKolInfoLocked])

  const durationBeginDate = dayjs().subtract(
    DURATION_MONTH_INTEGER_MAP[duration],
    'months',
  )

  const reelsData = useMemo((): {
    shortInteractive: Nullable<number>
    shortInteractiveRate: Nullable<number>
    avgShortLike: Nullable<number>
    avgShortComment: Nullable<number>
  } => {
    if (isKolInfoLocked) {
      return mockReelsData
    }

    if (isUndefined(kpi) || isUndefined(analyze) || isEmpty(analyze)) {
      return {
        shortInteractive: null,
        shortInteractiveRate: null,
        avgShortLike: null,
        avgShortComment: null,
      }
    }

    const shortInteractive =
      duration === Duration.ThreeMonths
        ? kpi.avgShortEngagementCount3m
        : kpi.avgShortEngagementCount6m
    const shortInteractiveRate =
      duration === Duration.ThreeMonths
        ? kpi.avgShortEngagementRate3m
        : kpi.avgShortEngagementRate6m
    const avgShortLike =
      duration === Duration.ThreeMonths
        ? kpi.avgShortLikeCount3m
        : kpi.avgShortLikeCount6m
    const avgShortComment =
      duration === Duration.ThreeMonths
        ? kpi.avgShortCommentCount3m
        : kpi.avgShortCommentCount6m

    return {
      shortInteractive,
      shortInteractiveRate,
      avgShortLike,
      avgShortComment,
    }
  }, [isKolInfoLocked, kpi, analyze, duration])

  const [series, chartOptions, hasChartData] = useMemo((): [
    Series[],
    ApexChartOptions<Series[]>,
    boolean,
  ] => {
    const [series, InteractiveSeriesData] = ((): [Series[], SeriesData[]] => {
      if (!kpi) {
        return [[], []]
      }

      const mapYValue = (
        data: {
          publishedAt: string
          totalAmount: number
        },
        value: number,
      ): SeriesData => {
        return {
          x: data.publishedAt,
          y: value,
          totalAmount: data.totalAmount,
        }
      }

      const durationValue = DURATION_MONTH_INTEGER_MAP[duration]
      const fullTimeData = mergeZeroData(
        inTimeBoundDate(
          isKolInfoLocked || !analyze
            ? mockFixedEngagementAnalysisData
            : analyze,
          durationValue,
          'publishedAt',
        ),
        durationValue,
      )

      const groupData = groupInteractiveByGranularity(
        fullTimeData,
        timeGranularity,
      )

      return [
        [
          {
            name: formatMessage({ id: 'kol:engagements_chart_illu_like' }),
            color: theme.colors.chart.blue3,
            data: groupData.map((g) => mapYValue(g, g.likeCount)),
          },
          {
            name: formatMessage({ id: 'kol:engagements_chart_illu_comment' }),
            color: theme.colors.chart.cyan1,
            data: groupData.map((g) => mapYValue(g, g.commentCount)),
          },
          ...(statisticPlatformSupport.avgShareCount.includes(
            platform.shortcode,
          )
            ? [
                {
                  name: formatMessage({ id: 'autoreport:metric_share' }),
                  color: theme.colors.chart.purple2,
                  data: groupData.map((g) => mapYValue(g, g.shareCount)),
                },
              ]
            : []),
          ...(statisticPlatformSupport.avgRepostAndQuoteCount.includes(
            platform.shortcode,
          )
            ? [
                {
                  name: formatMessage({
                    id: 'general:column_title_repost_and_quote_count',
                  }),
                  color: theme.colors.chart.gold4,
                  data: groupData.map((g) =>
                    mapYValue(g, g.repostAndQuoteCount),
                  ),
                },
              ]
            : []),
        ],
        groupData.map((g) => mapYValue(g, g.interactiveCount)),
      ]
    })()

    const hasChartData = series.some((item) =>
      item.data.some((data) => !!data.y),
    )

    const boundaryDates = getBoundaryDateSet(
      head(series)?.data.map((data) => data.x) ?? [],
    )

    const chartOptions: ApexChartOptions<Series[]> = {
      chart: {
        id: 'EngagementAnalysisChart',
        height: 300,
        stacked: true,
        toolbar: {
          show: false,
        },
        events: {
          click: (_event, _chartContext, options): void => {
            if (!options) {
              return
            }

            const { dataPointIndex, config } = options

            if (dataPointIndex !== -1) {
              const between = formatDuration(
                dayjs(config.series?.[0].data[dataPointIndex].x),
                timeGranularity,
                true,
                dataPointIndex === 0 ? durationBeginDate : undefined,
              )

              const [postStartDate, postEndDate] = between.split(' ~ ')
              const [postStartTime, postEndTime] = getStartEndIsoStrings([
                postStartDate,
                postEndDate,
              ])

              setSearchParams(
                {
                  ...searchParams.data,
                  ...initialPostFilterConditions,
                  postListDateRangeStart: postStartTime,
                  postListDateRangeEnd: postEndTime,
                  postListPlatform: platform.shortcode,
                },
                'postList',
              ).then(noop)
            }
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          columnWidth: 18,
          dataLabels: {
            maxItems: 10,
            position: 'top',
            total: {
              enabled: true,
              style: {
                color: theme.colors.text.primary,
                fontSize: '12px',
                fontWeight: 400,
              },
              formatter: (val?: number | string): string => {
                return isNumber(val) && val > 0
                  ? formatNumber(val, {
                      isCompactDisplay: true,
                    })
                  : ''
              },
            },
          },
        },
      },
      xaxis: {
        labels: {
          rotateAlways: boundaryDates.size > 1,
          formatter: (value, _timestamp, opts): string => {
            const formatType = ((): FormatDateType => {
              if (timeGranularity === 'month') {
                return boundaryDates.has(value)
                  ? 'localeYearMonth'
                  : 'localeMonth'
              }
              return boundaryDates.has(value) ? 'default' : 'numericMonthDay'
            })()

            if (timeGranularity === 'week') {
              return formatDate(
                opts?.i === 0
                  ? durationBeginDate.toISOString()
                  : dayjs(value).startOf('week').toISOString(),
                formatType,
              )
            }

            return formatDate(value, formatType)
          },
        },
      },
      yaxis: {
        tickAmount: 3,
        labels: {
          show: false,
          formatter: (val: number): string => {
            return formatNumber(val, {
              isCompactDisplay: true,
            })
          },
        },
      },
      legend: {
        show: false,
      },
      tooltip: {
        custom: (opts) => {
          const { seriesIndex, dataPointIndex, w } = opts
          const currentSeriesData =
            w.config.series?.[seriesIndex].data[dataPointIndex]

          return ReactDOMServer.renderToString(
            <CustomApexChartTooltip
              content={{
                extraInfo: {
                  name: formatMessage({ id: 'kol:engagements_chart_illu_sum' }),
                  value: formatNumber(InteractiveSeriesData[dataPointIndex].y, {
                    isCompactDisplay: true,
                  }),
                },
                seriesData:
                  opts.w.config.series?.map(({ name, color, data }) => ({
                    name,
                    color,
                    value: formatNumber(data[dataPointIndex].y, {
                      isCompactDisplay: true,
                    }),
                  })) ?? [],
              }}
              title={{
                dateRange: formatDuration(
                  dayjs(currentSeriesData?.x),
                  timeGranularity,
                  false,
                  dataPointIndex === 0 ? durationBeginDate : undefined,
                ),
                postCount: formatMessage(
                  { id: 'kol:chart_post_unit' },
                  {
                    n: currentSeriesData?.totalAmount,
                  },
                ),
                hint: formatMessage({
                  id: 'irm:kol_detail_chart_click_search_content',
                }),
              }}
            />,
          )
        },
      },
    }

    return [series, chartOptions, hasChartData]
  }, [
    kpi,
    duration,
    isKolInfoLocked,
    analyze,
    timeGranularity,
    formatMessage,
    platform.shortcode,
    durationBeginDate,
    setSearchParams,
    searchParams.data,
    formatNumber,
    formatDate,
    theme,
  ])

  const formatMetricData = (
    metric: number,
    isPercentageDisplay?: boolean,
  ): string => {
    if (isPercentageDisplay) {
      return formatNumber(metric, {
        isPercentageDisplay,
      })
    }

    return formatNumber(Math.round(metric))
  }

  const {
    interactiveRatePerformance,
    avgCommentLikeRatePerformance,
    avgShortEngagementRatePerformance,
  } = useMemo(() => {
    if (isUndefined(kpi)) {
      return {
        interactiveRatePerformance: mockKolMetricPerformanceNullData,
        avgCommentLikeRatePerformance: mockKolMetricPerformanceNullData,
        avgShortEngagementRatePerformance: mockKolMetricPerformanceNullData,
      }
    }

    if (isKolInfoLocked) {
      return {
        interactiveRatePerformance: mockKolInfoKpi.interactiveRatePerformance3m,
        avgCommentLikeRatePerformance:
          mockKolInfoKpi.avgCommentLikeRatePerformance3m,
        avgShortEngagementRatePerformance:
          mockKolInfoKpi.avgShortEngagementRatePerformance3m,
      }
    }

    if (duration === Duration.ThreeMonths) {
      return {
        interactiveRatePerformance: kpi.interactiveRatePerformance3m,
        avgCommentLikeRatePerformance: kpi.avgCommentLikeRatePerformance3m,
        avgShortEngagementRatePerformance:
          kpi.avgShortEngagementRatePerformance3m,
      }
    }

    return {
      interactiveRatePerformance: kpi.interactiveRatePerformance6m,
      avgCommentLikeRatePerformance: kpi.avgCommentLikeRatePerformance6m,
      avgShortEngagementRatePerformance:
        kpi.avgShortEngagementRatePerformance6m,
    }
  }, [duration, isKolInfoLocked, kpi])

  const { interactiveRatePr, avgCommentLikeRatePr, avgShortEngagementRatePr } =
    useMemo(
      () =>
        isKolInfoLocked
          ? mockKolPr
          : {
              interactiveRatePr: evaluateMetricPR(
                generalData?.interactiveRate,
                interactiveRatePerformance,
              ),
              avgCommentLikeRatePr: evaluateMetricPR(
                generalData?.likeToCommentRatio,
                avgCommentLikeRatePerformance,
              ),
              avgShortEngagementRatePr: evaluateMetricPR(
                reelsData.shortInteractiveRate,
                avgShortEngagementRatePerformance,
              ),
            },
      [
        avgCommentLikeRatePerformance,
        avgShortEngagementRatePerformance,
        generalData?.interactiveRate,
        generalData?.likeToCommentRatio,
        interactiveRatePerformance,
        isKolInfoLocked,
        reelsData.shortInteractiveRate,
      ],
    )

  return (
    <KolDetailDataBlock
      colSpan={24}
      header={{
        title: formatMessage(
          {
            id: 'kol:engagement_analysis_data_block_title',
          },
          { platformTitle: platform.title },
        ),
      }}
      kolId={kolInfo?.id}
      kolMetrics='Engagements'
      kolName={kolInfo?.name}
      platform={platform.shortcode}
    >
      <Wrapper>
        <StatisticsWrapper>
          <StatisticsWrapperGrid>
            <StyledKolDetailStatistic
              loading={statisticLoading}
              suffix={
                !isUndefined(interactiveRatePr) &&
                !isNull(interactiveRatePerformance?.pr50) && (
                  <Tooltip
                    placement='bottom'
                    title={
                      !hiddenData &&
                      formatMessage(
                        { id: 'kol:platform_performance_chart_pr50' },
                        {
                          metricType: formatMessage({
                            id: 'kol:platform_data_avg_eng_rate',
                          }),
                          span: (text) => <span>{text}</span>,
                          pr50: formatNumber(interactiveRatePerformance?.pr50, {
                            isPercentageDisplay: true,
                          }),
                        },
                      )
                    }
                  >
                    <MetricPerformance color={interactiveRatePr.color}>
                      <Blur activate={hiddenData}>
                        {formatMessage({ id: interactiveRatePr.rating })}
                      </Blur>
                    </MetricPerformance>
                  </Tooltip>
                )
              }
              title={formatMessage({ id: 'kol:platform_data_avg_eng_rate' })}
              tooltip={{
                title: formatMessage(
                  {
                    id: 'kol:platform_data_avg_eng_rate_tooltip',
                  },
                  { n: DURATION_MONTH_INTEGER_MAP[duration] },
                ),
              }}
              value={
                isNil(generalData?.interactiveRate) ||
                generalData.interactiveRate === 0
                  ? null
                  : formatMetricData(generalData.interactiveRate, true)
              }
            />
            <StyledKolDetailStatistic
              loading={statisticLoading}
              suffix={
                !isUndefined(avgCommentLikeRatePr) &&
                !isNull(avgCommentLikeRatePerformance?.pr50) && (
                  <Tooltip
                    placement='bottom'
                    title={
                      !hiddenData &&
                      formatMessage(
                        { id: 'kol:platform_performance_chart_pr50' },
                        {
                          metricType: formatMessage({
                            id: 'kol:platform_data_avg_eng_rate',
                          }),
                          span: (text) => <span>{text}</span>,
                          pr50: `${formatNumber(
                            avgCommentLikeRatePerformance.pr50 * 100,
                            {
                              maximumFractionDigits: 2,
                            },
                          )}
                          : 100`,
                        },
                      )
                    }
                  >
                    <MetricPerformance color={avgCommentLikeRatePr.color}>
                      <Blur activate={hiddenData}>
                        {formatMessage({ id: avgCommentLikeRatePr.rating })}
                      </Blur>
                    </MetricPerformance>
                  </Tooltip>
                )
              }
              title={formatMessage({
                id: 'kol:platform_data_avg_comment_to_like_rate',
              })}
              tooltip={{
                title: formatMessage(
                  {
                    id: 'kol:platform_data_avg_comment_to_like_tooltip',
                  },
                  { n: DURATION_MONTH_INTEGER_MAP[duration] },
                ),
              }}
              value={
                isNil(generalData?.likeToCommentRatio) ||
                generalData.likeToCommentRatio === 0 ? null : (
                  <Blur activate={hiddenData} blurValue={5}>
                    {formatMessage(
                      { id: 'general:like_comment_ratio' },
                      {
                        ratio: formatNumber(
                          generalData.likeToCommentRatio * 100,
                          {
                            maximumFractionDigits: 2,
                          },
                        ),
                      },
                    )}
                  </Blur>
                )
              }
            />
            <div />
            <StyledKolDetailStatistic
              loading={statisticLoading}
              title={formatMessage({
                id: 'kol:data_from_kol_avg_eng',
              })}
              tooltip={{
                title: formatMessage(
                  { id: 'kol:platform_data_avg_post_eng_tooltip' },
                  { n: DURATION_MONTH_INTEGER_MAP[duration] },
                ),
              }}
              value={
                isNil(generalData?.interactive) || generalData.interactive === 0
                  ? null
                  : formatMetricData(generalData.interactive)
              }
            />
            <StyledKolDetailStatistic
              loading={statisticLoading}
              title={formatMessage({
                id: 'kol:card_avg_like',
              })}
              tooltip={{
                title: formatMessage(
                  { id: 'kol:platform_data_avg_like_tooltip' },
                  { n: DURATION_MONTH_INTEGER_MAP[duration] },
                ),
              }}
              value={
                isNil(generalData?.avgLike) || generalData.avgLike === 0
                  ? null
                  : formatMetricData(generalData.avgLike)
              }
            />
            <StyledKolDetailStatistic
              loading={statisticLoading}
              title={formatMessage({
                id: 'kol:card_avg_comment',
              })}
              tooltip={{
                title: formatMessage(
                  { id: 'kol:platform_data_avg_comment_tooltip' },
                  { n: DURATION_MONTH_INTEGER_MAP[duration] },
                ),
              }}
              value={
                isNil(generalData?.avgComment) || generalData.avgComment === 0
                  ? null
                  : formatMetricData(generalData.avgComment)
              }
            />
          </StatisticsWrapperGrid>
          {(statisticPlatformSupport.avgRepostAndQuoteCount.includes(
            platform.shortcode,
          ) ||
            statisticPlatformSupport.avgShareCount.includes(
              platform.shortcode,
            )) && (
            <StatisticsWrapperGrid>
              {statisticPlatformSupport.avgShareCount.includes(
                platform.shortcode,
              ) && (
                <StyledKolDetailStatistic
                  loading={statisticLoading}
                  title={formatMessage({
                    id: 'project:metric_avg_share',
                  })}
                  tooltip={{
                    title: formatMessage({
                      id: 'project:metric_avg_share_tooltip',
                    }),
                  }}
                  value={
                    isNil(generalData?.shareCount) ||
                    generalData.shareCount === 0
                      ? null
                      : formatMetricData(generalData.shareCount)
                  }
                />
              )}
              {statisticPlatformSupport.avgRepostAndQuoteCount.includes(
                platform.shortcode,
              ) && (
                <StyledKolDetailStatistic
                  loading={statisticLoading}
                  title={formatMessage({
                    id: 'general:column_title_avg_repost_and_quote_count',
                  })}
                  tooltip={{
                    title: formatMessage(
                      {
                        id: 'general:column_title_avg_repost_and_quote_count_tooltip',
                      },
                      { n: DURATION_MONTH_INTEGER_MAP[duration] },
                    ),
                  }}
                  value={
                    isNil(generalData?.avgRepostAndQuoteCount) ||
                    generalData.avgRepostAndQuoteCount === 0 ? null : (
                      <Blur activate={hiddenData}>
                        {formatMetricData(generalData.avgRepostAndQuoteCount)}
                      </Blur>
                    )
                  }
                />
              )}
            </StatisticsWrapperGrid>
          )}
          {PlatformShortcode.Instagram === platform.shortcode && (
            <>
              <Divider style={{ margin: '10px 0 0 0' }} />
              <KolDetailDataBlockSubHeader
                title={formatMessage({ id: 'kol:reels_engagement_metric' })}
              />
              <Flex gap={20}>
                <StyledKolDetailStatistic
                  loading={statisticLoading}
                  suffix={
                    !isNull(avgShortEngagementRatePerformance?.pr50) &&
                    !isUndefined(avgShortEngagementRatePr) && (
                      <Tooltip
                        placement='bottom'
                        title={
                          !hiddenData &&
                          formatMessage(
                            { id: 'kol:platform_performance_chart_pr50' },
                            {
                              metricType: formatMessage({
                                id: 'kol:platform_data_avg_eng_rate',
                              }),
                              span: (text) => <span>{text}</span>,
                              pr50: formatNumber(
                                avgShortEngagementRatePerformance.pr50,
                                {
                                  isPercentageDisplay: true,
                                },
                              ),
                            },
                          )
                        }
                      >
                        <MetricPerformance
                          color={avgShortEngagementRatePr.color}
                        >
                          <Blur activate={hiddenData}>
                            {formatMessage({
                              id: avgShortEngagementRatePr.rating,
                            })}
                          </Blur>
                        </MetricPerformance>
                      </Tooltip>
                    )
                  }
                  title={formatMessage({
                    id: 'kol:reels_interactive_rate',
                  })}
                  tooltip={{
                    title: formatMessage(
                      { id: 'kol:reels_interactive_rate_tooltip' },
                      { n: DURATION_MONTH_INTEGER_MAP[duration] },
                    ),
                  }}
                  value={
                    isNil(reelsData.shortInteractiveRate) ||
                    reelsData.shortInteractiveRate === 0 ? null : (
                      <Blur activate={hiddenData}>
                        {formatMetricData(reelsData.shortInteractiveRate, true)}
                      </Blur>
                    )
                  }
                />
                <StyledKolDetailStatistic
                  loading={statisticLoading}
                  title={formatMessage({
                    id: 'kol:reels_interactive',
                  })}
                  tooltip={{
                    title: formatMessage(
                      { id: 'kol:reels_interactive_tooltip' },
                      { n: DURATION_MONTH_INTEGER_MAP[duration] },
                    ),
                  }}
                  value={
                    isNil(reelsData.shortInteractive) ||
                    reelsData.shortInteractive === 0 ? null : (
                      <Blur activate={hiddenData}>
                        {formatMetricData(reelsData.shortInteractive)}
                      </Blur>
                    )
                  }
                />
                <StyledKolDetailStatistic
                  loading={statisticLoading}
                  title={formatMessage({
                    id: 'kol:reels_avg_like',
                  })}
                  tooltip={{
                    title: formatMessage(
                      { id: 'kol:reels_avg_like_tooltip' },
                      { n: DURATION_MONTH_INTEGER_MAP[duration] },
                    ),
                  }}
                  value={
                    isNil(reelsData.avgShortLike) ||
                    reelsData.avgShortLike === 0 ? null : (
                      <Blur activate={hiddenData}>
                        {formatMetricData(reelsData.avgShortLike)}
                      </Blur>
                    )
                  }
                />
              </Flex>
              <StatisticsWrapperGrid>
                <StyledKolDetailStatistic
                  loading={statisticLoading}
                  title={formatMessage({
                    id: 'kol:reels_avg_comment',
                  })}
                  tooltip={{
                    title: formatMessage(
                      { id: 'kol:reels_avg_comment_tooltip' },
                      { n: DURATION_MONTH_INTEGER_MAP[duration] },
                    ),
                  }}
                  value={
                    isNil(reelsData.avgShortComment) ||
                    reelsData.avgShortComment === 0 ? null : (
                      <Blur activate={hiddenData}>
                        {formatMetricData(reelsData.avgShortComment)}
                      </Blur>
                    )
                  }
                />
              </StatisticsWrapperGrid>
            </>
          )}
        </StatisticsWrapper>
        <ChartWrapper>
          <KolDetailDataBlockSubHeader
            extraContent={
              <TimeGranularitySelector
                disabled={!hasChartData}
                value={hasChartData ? timeGranularity : undefined}
                onChange={setTimeGranularity}
              />
            }
            title={formatMessage(
              {
                id: 'kol:engagements_chart_title',
              },
              { platform: '' },
            )}
            tooltip={{
              title: formatMessage({ id: 'kol:view_live_compare_subtitle' }),
            }}
          />
          {isFetchingKolInfo ? (
            <Flex align='center' justify='center' style={{ flex: 1 }}>
              <Spin />
            </Flex>
          ) : (
            <Spin
              indicator={<Blocker />}
              spinning={isPublicKolDetailPage}
              style={{ overflow: 'auto' }}
            >
              <Blur activate={hiddenData}>
                {hasChartData ? (
                  <ChartContentWrapper>
                    <Flex style={{ overflow: 'hidden' }}>
                      <ApexChartsYAxisLabels
                        options={chartOptions}
                        series={series}
                        type='bar'
                      />
                      <div style={{ overflow: 'auto hidden', flex: 1 }}>
                        <ChartContent
                          width={GRANULARITY_CHART_WIDTH_MAP[timeGranularity]}
                        >
                          <ApexCharts
                            options={chartOptions}
                            series={series}
                            type='bar'
                          />
                        </ChartContent>
                      </div>
                    </Flex>
                    <LegendWrapper>
                      {series.map((s) => (
                        <Flex gap={6} key={s.name}>
                          <LegendColor color={s.color} />
                          <LegendLabel> {s.name} </LegendLabel>
                        </Flex>
                      ))}
                    </LegendWrapper>
                  </ChartContentWrapper>
                ) : (
                  <NoData />
                )}
              </Blur>
            </Spin>
          )}
        </ChartWrapper>
      </Wrapper>
    </KolDetailDataBlock>
  )
}

export default EngagementAnalysis

const StyledKolDetailStatistic = styled(KolDetailStatistic)`
  word-break: normal;
  overflow-wrap: anywhere;

  ${BottomItems} {
    flex-wrap: wrap;
  }
`

const StatisticsWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 20px;
  border-right: 1px solid ${({ theme }): string => theme.colors.base.grey.grey6};
  padding-right: 20px;
  ${StyledKolDetailStatistic} {
    flex: 1 1 150px;
  }

  @media (max-width: 980px) {
    border-right: none;
  }
`

const StatisticsWrapperGrid = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(3, 1fr);
  height: fit-content;
`

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  gap: 30px;

  ${StatisticsWrapper} {
    flex: 1 1 100%;
  }

  ${ChartWrapper} {
    flex: 1 1 100%;
  }

  @media (max-width: 980px) {
    flex-direction: column;
  }
`

const MetricPerformance = styled.div<{ color }>`
  color: ${({ color }): string => color};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  align-self: flex-end;
  line-height: 140%;
`
